import React, { useMemo } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS SVG PREMIUM ---
const IconGraph = () => (
    <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2zm2 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
);

/**
 * Componente modular de lista analítica para distribuciones (Riesgos y Compañías)
 * Reemplaza las rígidas tablas por renglones Flexbox fluidos y espaciosos con efectos hover
 */
const AnalyticsList = ({ title, icon, data }) => (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-[0_2px_12px_-3px_rgba(0,0,0,0.02)] overflow-hidden text-left flex flex-col h-full transition-all duration-200 hover:shadow-md">
        {/* Cabecera superior con degradado suave */}
        <div className="p-4 px-5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 font-black text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-2 select-none">
            {icon} 
            <span>{title}</span>
        </div>
        
        {/* Cuerpo de la Lista */}
        <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto custom-scrollbar bg-white">
            {data.length === 0 ? (
                <div className="p-12 text-center text-slate-400 italic text-[11px] font-semibold select-none">
                    Sin registros para clasificar en este rango
                </div>
            ) : (
                data.map((row, idx) => (
                    <div 
                        key={idx} 
                        className="p-4 px-5 flex justify-between items-center hover:bg-slate-50/40 transition-colors duration-150 group"
                    >
                        {/* Izquierda: Ramo / Compañía (Nombre en negrita + Cantidad de pólizas abajo) */}
                        <div className="flex flex-col text-left min-w-0 pr-4">
                            <span className="text-[12px] font-black uppercase text-slate-800 tracking-tight truncate group-hover:text-blue-900 transition-colors">
                                {row.name}
                            </span>
                            {row.cant !== undefined && (
                                <span className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 tracking-wider select-none">
                                    {row.cant} {row.cant === 1 ? 'Póliza' : 'Pólizas'}
                                </span>
                            )}
                        </div>
                        
                        {/* Derecha: Valores de Primas (ARS en verde, USD en azul) */}
                        <div className="flex flex-col items-end shrink-0 select-none">
                            {row.ARS !== undefined && row.ARS > 0 && (
                                <span className="font-mono text-emerald-600 font-black text-[11px] tracking-tight">
                                    {formatCurrencyUniversal(row.ARS, '$')}
                                </span>
                            )}
                            {row.USD !== undefined && row.USD > 0 && (
                                <span className="font-mono text-blue-600 font-black text-[11px] tracking-tight mt-0.5">
                                    {formatCurrencyUniversal(row.USD, 'U$S')}
                                </span>
                            )}
                            {(!row.ARS && !row.USD) && <span className="text-slate-300 font-mono">-</span>}
                        </div>
                    </div>
                ))
            )}
        </div>
    </div>
);

/**
 * KpisPolizas - Dashboard analítico exclusivo para primas emitidas vigentes.
 */
const KpisPolizas = ({ registros, isDbLoaded }) => {

    const analytics = useMemo(() => {
        if (!isDbLoaded || !registros || registros.length === 0) return null;

        let primaARS = 0; let primaUSD = 0;
        const porRiesgo = {}; const porCompania = {};

        registros.forEach(p => {
            const rawPrima = p.primaEmitidaRefacturacion !== undefined ? p.primaEmitidaRefacturacion : p.primaEmitida;
            const prima = Math.abs(parseCurrency(rawPrima));
            const monedaRaw = String(p.moneda || p.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            if (esUSD) primaUSD += prima; else primaARS += prima;

            const r = p.riesgo || 'SIN ESPECIFICAR';
            if (!porRiesgo[r]) porRiesgo[r] = { name: r, cant: 0, ARS: 0, USD: 0 };
            porRiesgo[r].cant += 1; if (esUSD) porRiesgo[r].USD += prima; else porRiesgo[r].ARS += prima;

            const c = p.compania || 'SIN ESPECIFICAR';
            if (!porCompania[c]) porCompania[c] = { name: c, cant: 0, ARS: 0, USD: 0 };
            porCompania[c].cant += 1; if (esUSD) porCompania[c].USD += prima; else porCompania[c].ARS += prima;
        });

        return {
            primaARS,
            primaUSD,
            cantidadPolizas: registros.length,
            listRiesgo: Object.values(porRiesgo).sort((a, b) => b.cant - a.cant),
            listCompania: Object.values(porCompania).sort((a, b) => b.cant - a.cant)
        };
    }, [registros, isDbLoaded]);

    if (!isDbLoaded) {
        return <div className="p-8 text-center text-slate-400 italic text-sm">Cargando estadísticas de emisión...</div>;
    }

    if (!analytics) {
        return <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200">No hay datos en este rango para calcular emisiones.</div>;
    }

    return (
        <div className="space-y-8 text-left animate-fade-in">
            {/* CARDS DE KPIs SUPERIORES DE EMISIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* 1. EMISIÓN EN PESOS */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Prima Emitida (ARS)</p>
                        <h4 className="text-xl font-black mt-2 font-mono text-emerald-600">
                            {formatCurrencyUniversal(analytics.primaARS, '$')}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1 font-bold uppercase tracking-wider">Pesos Argentinos</p>
                    </div>
                    <span className="p-3 bg-emerald-50 text-emerald-600 rounded-xl shrink-0 font-mono font-black text-xs">$</span>
                </div>

                {/* 2. EMISIÓN EN DÓLARES */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Prima Emitida (USD)</p>
                        <h4 className="text-xl font-black mt-2 font-mono text-blue-600">
                            {formatCurrencyUniversal(analytics.primaUSD, 'U$S')}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1 font-bold uppercase tracking-wider">Dólares Estadounidenses</p>
                    </div>
                    <span className="p-3 bg-blue-50 text-blue-600 rounded-xl shrink-0 font-mono font-black text-xs">USD</span>
                </div>

                {/* 3. PÓLIZAS NETAS ACTIVAS */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Pólizas Netas Activas</p>
                        <h4 className="text-xl font-black mt-2 font-mono text-slate-800">
                            {analytics.cantidadPolizas}
                        </h4>
                        <p className="text-[9px] text-slate-400 mt-1 font-bold uppercase tracking-wider">Casos vigentes ingresados</p>
                    </div>
                    <span className="p-3 bg-slate-100 text-slate-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center font-mono font-black text-xs">#</span>
                </div>
            </div>

            {/* TABLAS PARALELAS DE RAMOS Y COMPAÑÍAS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsList 
                    title="Segmentación por Ramo / Riesgo"
                    icon={<IconGraph />}
                    data={analytics.listRiesgo}
                />
                <AnalyticsList 
                    title="Distribución por Compañía Aseguradora"
                    icon={<IconGraph />}
                    data={analytics.listCompania}
                />
            </div>
        </div>
    );
};

export default KpisPolizas;